import { useState } from "react";
import useCod from "../../../data/useCod";
import ConfirmModal from "../../shared/Confirm/ConfirmModal";
import useSingleLead from "../../../data/useSingleLead";
import useVehiclesMutations from "../../../data/useVehiclesMutations";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import { DateTime } from "luxon";
import useNotification from "../../notifications/useNotifications";
import LoadingWheel from "../../shared/LoadingWheel";
import { useQueryClient } from "@tanstack/react-query";


export default function CODStatus({ leadId, showAddress = false }: { leadId: string, showAddress?: boolean }) {
  const queryClient = useQueryClient();

  const { lead, downloadDoc } = useSingleLead(leadId, "CODStatus");
  const { update: updateVehicle } = useVehiclesMutations();
  const { runCod } = useCod();
  const { addNotification } = useNotification();
  const { saveHandlers } = useDefaultCRUDHandlers("Lead");

  const [confirmCodOpen, setConfirmCodOpen] = useState(false);

  const handleCodRequired = () => {
    let updatedVehicle = lead.data?.data.vehicle!;

    updatedVehicle.codRequired = !updatedVehicle.codRequired;

    updateVehicle.mutate(updatedVehicle, {
      ...saveHandlers,
      onSuccess: () => {
        saveHandlers.onSuccess();
        queryClient.invalidateQueries(["pagedTable"])
      },
    });
  };

  const handleCodConfirmed = (confirm: boolean) => {
    let updatedVehicle = lead.data?.data.vehicle!;
    const codConfirmedOn = confirm ? DateTime.now().toISO() : null;

    updatedVehicle.codConfirmedOn = codConfirmedOn;

    updateVehicle.mutate(updatedVehicle, {
      ...saveHandlers,
      onSuccess: () => {
        saveHandlers.onSuccess();
        queryClient.invalidateQueries(["pagedTable"])
      },
    });
  };

  const handleGenerateCod = () => {

    if (!lead.data?.data.vehicle?.line1 || !lead.data?.data.vehicle?.postcode) {
      addNotification({
        variant: "error",
        primaryText: "Cannot generate COD certificate. Please ensure the vehicle address has at least Address Line 1 and Postcode."
      });
      return;
    }

    runCod.mutate(lead.data?.data.id, {
      onSuccess: () => {
        saveHandlers.onSuccess();
        setConfirmCodOpen(false);
        queryClient.invalidateQueries(["pagedTable"])
      },
      onError: (error: any) => {
        //saveHandlers.onError();
        addNotification({
          variant: "error",
          primaryText: error.message,
        });
      }
    });
  }

  const downloadCodCertificate = () => {
    var ids = {
      leadId: lead.data?.data.id,
      docId: lead.data?.data.vehicle?.codCertificateDocument
    };

    downloadDoc.mutate(ids, {
      onSuccess: (file: any) => {
        const url = window.URL.createObjectURL(file);
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.type = 'image/png';
        link.download = `${lead.data?.data.vehicle?.vrm} COD Certificate.png`;
        link.click();
        URL.revokeObjectURL(url);
      }
    });
  }

  if (lead.isLoading) {
    return <LoadingWheel />
  }

  return (
    <>
      <ConfirmModal open={confirmCodOpen} setOpen={setConfirmCodOpen} title="Confirm COD" confirmButtonText="Generate COD"
        message="Are you sure you want to create a certificate of destruction for this vehicle? This process is irreversible"
        onConfirm={handleGenerateCod} isLoading={runCod.isLoading}
      />

      <div className="flex flex-col gap-y-4 divide-y">

        {showAddress && (
          <div className="grid grid-cols-2 gap-x-8">
            {[
              { label: 'Line 1', value: lead.data?.data.vehicle?.line1 },
              { label: 'Line 2', value: lead.data?.data.vehicle?.line2 },
              { label: 'Line 3', value: lead.data?.data.vehicle?.line3 },
              { label: 'Locality', value: lead.data?.data.vehicle?.locality },
              { label: 'Town', value: lead.data?.data.vehicle?.town },
              { label: 'County', value: lead.data?.data.vehicle?.county },
              { label: 'Postcode', value: lead.data?.data.vehicle?.postcode },
            ].map((field, index) => (
              <div key={index} className="grid grid-cols-2 gap-2 items-center py-2">
                <dt className="text-sm font-medium text-gray-500">
                  {field.label}
                </dt>
                <dd className="text-sm text-gray-900">
                  {field.value || <span className="text-gray-300 italic">Not provided</span>}
                </dd>
              </div>
            ))}
          </div>
        )}

        <div className="divide-y">

          <div className="py-2 grid grid-cols-2 gap-4 items-center">
            <dt className="text-sm font-medium text-gray-500">
              COD Required
            </dt>
            <dd className="flex justify-between items-center text-sm text-gray-900">
              <div>
                {lead.data?.data.vehicle?.codRequired ? "Yes" : "No"}
              </div>
              {!lead.data?.data.vehicle?.codCertificateNumber && (
                <button
                  type="button"
                  onClick={handleCodRequired}
                  className="ml-3 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-1 py-1 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700">
                  Toggle
                </button>
              )}
            </dd>
          </div>

          {(lead.data?.data.vehicle?.codRequired || lead.data?.data.vehicle?.codCertificateNumber) && (
            <>
              <div className="py-2 grid grid-cols-2 gap-4 items-center">
                <dt className="text-sm font-medium text-gray-500">
                  COD Confirmed
                </dt>
                <dd className="flex justify-between items-center text-sm text-gray-900">
                  <div>
                    {lead.data?.data.vehicle?.codConfirmedOn ? "Yes" : "No"}
                  </div>

                  {!lead.data?.data.vehicle?.codCertificateNumber && (
                    lead.data?.data.vehicle?.codConfirmedOn ? (
                      <button
                        type="button"
                        onClick={() => handleCodConfirmed(false)}
                        className="ml-3 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-1 py-1 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700">
                        Toggle
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleCodConfirmed(true)}
                        className="ml-3 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-1 py-1 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700">
                        Toggle
                      </button>
                    )
                  )}
                </dd>
              </div>

              <div className="py-2 grid grid-cols-2 gap-4 items-center">
                <dt className="text-sm font-medium text-gray-500">
                  {lead.data?.data.vehicle?.codCertificateNumber ? "COD Certificate Number" : "Generate COD"}
                </dt>
                <dd className="flex justify-between items-center text-sm text-gray-900">
                  <span>{lead.data?.data.vehicle?.codCertificateNumber ?? ""}</span>
                  {!lead.data?.data.vehicle?.codCertificateNumber ? (
                    <button
                      type="button"
                      onClick={() => setConfirmCodOpen(true)}
                      className="ml-3 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-1 py-1 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700">
                      Generate
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => downloadCodCertificate()}
                      className="ml-3 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-1 py-1 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700">
                      Download Certificate
                    </button>
                  )}
                </dd>
              </div>

            </>
          )}
        </div>

      </div>

    </>
  )
}